import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  message,
  Spin,
  Table,
  Select,
  InputNumber,
  Avatar,
  Tag,
  Space,
  Tooltip,
} from "antd";
import {
  PoweroffOutlined,
  LinkOutlined,
  SyncOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";

const { Option } = Select;

const ChannelSettings = () => {
  const { id } = useParams(); // channel code
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const authHeaders = {
    Authorization: `Bearer ${token}`,
  };

  const [loading, setLoading] = useState(true);
  const [otaDetails, setOtaDetails] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [pmsRooms, setPmsRooms] = useState([]);
  const [otaRooms, setOtaRooms] = useState([]);
  const [pricing, setPricing] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPmsRooms = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/roomtype/listings/`, {
        headers: authHeaders,
      });

      const roomTypes = res?.data?.roomTypes;
      if (roomTypes) {
        setPmsRooms(Object.values(roomTypes));
      } else {
        setPmsRooms([]);
      }
    } catch (error) {
      console.error("Error fetching PMS rooms:", error);
      setPmsRooms([]);
    }
  }, [apiUrl, token]);

  const fetchOtaRooms = useCallback(
    async (hotelId) => {
      if (!hotelId) {
        setOtaRooms([]);
        return;
      }

      try {
        const res = await axios.post(
          `${apiUrl}/channels/mapping-details`,
          { hotelId, channel: id },
          { headers: authHeaders }
        );

        const otaRoomData = res?.data?.data?.rooms || [];
        const mappedRooms = otaRoomData.map((r) => ({
          id: r.id,
          title: r.title,
        }));

        setOtaRooms(mappedRooms);
      } catch (error) {
        console.error("Error fetching OTA rooms:", error);
        setOtaRooms([]);
      }
    },
    [apiUrl, id, token]
  );

  const fetchChannelDetails = useCallback(async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${apiUrl}/channels/list?code=${id}`, {
        headers: authHeaders,
      });

      const channelData = res?.data?.data?.[0];

      if (!channelData) {
        message.error("Channel not found.");
        navigate("/channel-manager");
        return;
      }

      setOtaDetails(channelData);

      const settings = channelData.settings || {};

      const mappingArray = settings.mapping
        ? Object.entries(settings.mapping).map(([pmsRoomId, otaRoomId]) => ({
            key: `${pmsRoomId}-${otaRoomId}`,
            pmsRoomId,
            otaRoomId,
          }))
        : [];

      setMappings(mappingArray);
      setPricing(settings.pricing || {});

      await Promise.all([fetchPmsRooms(), fetchOtaRooms(channelData.hotel_id)]);
    } catch (error) {
      console.error("Error loading channel:", error);
      message.error(error?.response?.data?.message || "Failed to load channel settings.");
    } finally {
      setLoading(false);
    }
  }, [apiUrl, id, navigate, fetchPmsRooms, fetchOtaRooms, token]);

  useEffect(() => {
    if (!apiUrl) {
      message.error("Missing REACT_APP_API_URL.");
      return;
    }

    if (!token) {
      message.error("Missing login token.");
      return;
    }

    fetchChannelDetails();
  }, [apiUrl, token, fetchChannelDetails]);

  const handleSyncNow = async (type = "bookings") => {
    try {
      setSyncing(true);

      const res = await axios.post(
        `${apiUrl}/channels/sync-booking-revisions`,
        {
          provider_code: "channex",
          channel_code: id,
          type,
        },
        {
          headers: authHeaders,
        }
      );

      message.success(res?.data?.message || "Sync started successfully.");
      await fetchChannelDetails();
    } catch (error) {
      console.error("Sync Now Error:", error);
      message.error(error?.response?.data?.message || "Failed to pull bookings.");
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);

      const mappingObject = mappings.reduce((acc, item) => {
        if (item.pmsRoomId && item.otaRoomId) {
          acc[item.pmsRoomId] = item.otaRoomId;
        }
        return acc;
      }, {});

      const payload = {
        channel_code: id,
        hotel_id: otaDetails?.hotel_id || null,
        settings: {
          mapping: mappingObject,
          pricing,
        },
      };

      await axios.post(`${apiUrl}/channels/connects`, payload, {
        headers: authHeaders,
      });

      message.success("Channel settings updated successfully.");
      await fetchChannelDetails();
    } catch (error) {
      console.error("Error saving settings:", error);
      message.error(error?.response?.data?.message || "Failed to save channel settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleConnectionChange = async (action) => {
    try {
      setLoading(true);

      if (action === "disconnect") {
        await axios.post(
          `${apiUrl}/channels/disconnect`,
          { channel_code: id },
          { headers: authHeaders }
        );

        message.success("Channel disconnected successfully.");
      } else {
        const payload = {
          channel_code: id,
          hotel_id: otaDetails?.hotel_id || null,
          settings: otaDetails?.settings || {},
        };

        await axios.post(`${apiUrl}/channels/connects`, payload, {
          headers: authHeaders,
        });

        message.success("Channel reconnected successfully.");
      }

      await fetchChannelDetails();
    } catch (error) {
      console.error("Connection change error:", error);
      message.error(error?.response?.data?.message || "Failed to update connection status.");
    } finally {
      setLoading(false);
    }
  };

  const updateMappingRow = (index, field, value) => {
    const updated = [...mappings];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };

    if (!updated[index].key) {
      updated[index].key = `${Date.now()}-${index}`;
    }

    setMappings(updated);
  };

  const mappingColumns = [
    {
      title: "PMS Room",
      dataIndex: "pmsRoomId",
      key: "pmsRoomId",
      render: (pmsRoomId, record, index) => (
        <Select
          value={pmsRoomId}
          onChange={(val) => updateMappingRow(index, "pmsRoomId", val)}
          style={{ width: "100%" }}
          placeholder="Select PMS Room"
          showSearch
          optionFilterProp="children"
        >
          {pmsRooms.map((room) => (
            <Option key={room.room_type_id} value={room.room_type_id}>
              {room.type_name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "OTA Room",
      dataIndex: "otaRoomId",
      key: "otaRoomId",
      render: (otaRoomId, record, index) => (
        <Select
          value={otaRoomId}
          onChange={(val) => updateMappingRow(index, "otaRoomId", val)}
          style={{ width: "100%" }}
          placeholder="Select OTA Room"
          showSearch
          optionFilterProp="children"
        >
          {otaRooms.map((room) => (
            <Option key={room.id} value={room.id}>
              {room.title}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record, index) => (
        <Button
          type="link"
          danger
          onClick={() => setMappings((prev) => prev.filter((_, i) => i !== index))}
        >
          Remove
        </Button>
      ),
    },
  ];

  if (loading || !otaDetails) {
    return <Spin size="large" style={{ display: "block", margin: "100px auto" }} />;
  }

  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid">
        <div className="page-header d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <Avatar src={otaDetails.logo} size={56} className="me-3">
              {!otaDetails.logo ? (otaDetails.name || id || "?").charAt(0).toUpperCase() : null}
            </Avatar>

            <div>
              <h3 className="page-title m-0">{otaDetails.name || id}</h3>
              <small className="text-muted">
                API: {otaDetails.api_provider || "N/A"}
              </small>

              <div className="mt-1">
                <Tag color={otaDetails.is_connected ? "green" : "volcano"}>
                  {otaDetails.is_connected ? "Connected" : "Disconnected"}
                </Tag>

                {otaDetails.last_synced_at && (
                  <Tooltip title={`Last sync at ${otaDetails.last_synced_at}`}>
                    <Tag color="blue" icon={<SyncOutlined />}>
                      Last Sync: {moment(otaDetails.last_synced_at).fromNow()}
                    </Tag>
                  </Tooltip>
                )}

                {otaDetails.error_message && (
                  <Tooltip title={otaDetails.error_message}>
                    <Tag color="red" icon={<InfoCircleOutlined />}>
                      Sync Error
                    </Tag>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>

          <Space wrap>
            {otaDetails.is_connected ? (
              <Button
                danger
                icon={<PoweroffOutlined />}
                onClick={() => handleConnectionChange("disconnect")}
              >
                Disconnect
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<LinkOutlined />}
                onClick={() => handleConnectionChange("reconnect")}
              >
                Reconnect
              </Button>
            )}

            {otaDetails.is_connected && (
              <Button
                icon={syncing ? <LoadingOutlined spin /> : <SyncOutlined />}
                type="default"
                onClick={() => handleSyncNow("bookings")}
                disabled={syncing}
              >
                {syncing ? "Syncing Bookings..." : "Sync Now"}
              </Button>
            )}

            <Button onClick={() => navigate("/channel-manager")}>Back</Button>
          </Space>
        </div>

        <Row gutter={16}>
          <Col span={24}>
            <Card
              title="Room Type Mappings"
              extra={
                <Button type="primary" loading={saving} onClick={handleSaveChanges}>
                  Save Changes
                </Button>
              }
              style={{ marginBottom: 24 }}
            >
              <Table
                columns={mappingColumns}
                dataSource={mappings}
                pagination={false}
                rowKey="key"
              />

              <Button
                type="dashed"
                onClick={() =>
                  setMappings((prev) => [
                    ...prev,
                    {
                      key: `${Date.now()}-${prev.length}`,
                      pmsRoomId: undefined,
                      otaRoomId: undefined,
                    },
                  ])
                }
                style={{ marginTop: 16 }}
              >
                Add New Mapping
              </Button>
            </Card>
          </Col>

          <Col span={24}>
            <Card
              title="Pricing Rules"
              extra={
                <Button type="primary" loading={saving} onClick={handleSaveChanges}>
                  Save Changes
                </Button>
              }
            >
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <label>Markup Type</label>
                  <Select
                    value={pricing.markupType}
                    onChange={(val) => setPricing((prev) => ({ ...prev, markupType: val }))}
                    style={{ width: "100%" }}
                    placeholder="Select markup type"
                  >
                    <Option value="percentage">Percentage</Option>
                    <Option value="flat">Flat</Option>
                  </Select>
                </Col>

                <Col xs={24} md={8}>
                  <label>Markup Value</label>
                  <InputNumber
                    min={0}
                    value={pricing.markupValue}
                    onChange={(val) => setPricing((prev) => ({ ...prev, markupValue: val }))}
                    style={{ width: "100%" }}
                    placeholder="Enter markup value"
                  />
                </Col>

                <Col xs={24} md={8}>
                  <label>Currency</label>
                  <Select
                    value={pricing.currencySync}
                    onChange={(val) => setPricing((prev) => ({ ...prev, currencySync: val }))}
                    style={{ width: "100%" }}
                    placeholder="Select currency"
                  >
                    <Option value="INR">INR</Option>
                    <Option value="USD">USD</Option>
                    <Option value="EUR">EUR</Option>
                    <Option value="GBP">GBP</Option>
                  </Select>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ChannelSettings;